/**
 * CSRF Token
 *
 * Single source of truth for the current CSRF token on the front end.
 *
 * Pages are served from the Blitz/Cloudflare cache, so the token can no longer
 * be printed into the HTML: rendering it would start a PHP session, emit
 * `Set-Cookie`, and make the response uncacheable at the edge. Instead the
 * token is fetched on demand from `/csrf` (excluded from Blitz caching) and
 * memoised for the life of the page.
 */

const CSRF_ENDPOINT = '/csrf';
const FETCH_TIMEOUT_MS = 5000;

let cachedToken = null;
let inFlight = null;

/**
 * Overwrite the memoised token (e.g. after a form refresh has fetched one).
 * @param {string|null} token
 */
function setCsrfToken(token) {
    cachedToken = (token || '').trim() || null;
}

/**
 * Resolve a CSRF token, fetching one if we don't hold a usable value.
 *
 * Concurrent callers share a single in-flight request so a page that fires
 * several requests at once doesn't stampede the endpoint.
 *
 * @param {Object} [options]
 * @param {boolean} [options.force] Bypass the memoised value and refetch.
 * @returns {Promise<string|null>} The token, or null if it could not be fetched.
 */
async function getCsrfToken({ force = false } = {}) {
    if (!force && cachedToken) return cachedToken;
    if (inFlight) return inFlight;

    inFlight = (async () => {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

            const response = await fetch(CSRF_ENDPOINT, {
                signal: controller.signal,
                credentials: 'same-origin',
                headers: { 'Accept': 'text/plain' },
            });

            clearTimeout(timeoutId);

            if (!response.ok) return cachedToken;

            const token = (await response.text()).trim();
            if (token) cachedToken = token;

            return cachedToken;
        } catch {
            // Network failure or timeout: fall back to whatever we already hold.
            return cachedToken;
        } finally {
            inFlight = null;
        }
    })();

    return inFlight;
}

export {
    getCsrfToken,
    setCsrfToken,
};
